import * as React from 'react';

import animateScrollTo from 'animated-scroll-to';

import { TabItem } from './TabItem/TabItem';
import { ITabItemInternalProps, ITabItemExternalProps } from './TabItem/types';

import { ITabsState, ITabsProps } from './types';

class Tabs extends React.Component<ITabsProps, ITabsState> {

	static Tab: React.ComponentType<ITabItemExternalProps> = TabItem;

	static defaultProps: Partial<ITabsProps> = {
		withContent: true,
		shouldCenterActiveTab: false,
	};

	state: ITabsState = {
		activeIndex: this.props.activeTab || 0,
	};

	navRef: React.RefObject<HTMLElement> = React.createRef();

	async componentDidMount(): Promise<void> {
		await this.selectActiveTab();
	}

	async componentDidUpdate(prevProps: Readonly<ITabsProps>): Promise<void> {
		if (prevProps.activeTab !== this.props.activeTab) {
			this.setState({ activeIndex: this.props.activeTab });
			await this.selectActiveTab();
		}
	}

	isActive = (index: number) => this.state.activeIndex === index;

	selectActiveTab = async () => {
		const { activeTab } = this.props;

		if (activeTab >= 0 && this.navRef.current) {
			const tab = this.navRef.current.childNodes[activeTab] as HTMLElement;

			if (tab) {
				await this.centerActiveTab(tab);
			}
		}
	};

	async centerActiveTab(activeTab: HTMLElement) {
		if (!this.props.shouldCenterActiveTab) {
			return;
		}
		const nav = this.navRef.current;
		const scrollLeft = activeTab.offsetLeft - nav.offsetLeft + (activeTab.offsetWidth / 2) - (nav.offsetWidth / 2);

		if (this.props.hasCenterActiveTabAnimation) {
			await animateScrollTo([scrollLeft, null], {
				maxDuration: 3000,
				minDuration: 350,
				elementToScroll: nav,
				speed: 300,
				horizontalOffset: this.props.horizontalAnimationOffset ? this.props.horizontalAnimationOffset / 2 : 0,
			});
		} else {
			nav.scrollLeft = scrollLeft;
		}
	}

	onClick = async (index: number, event: React.MouseEvent) => {
		await this.centerActiveTab(event.target as HTMLElement);
		this.setState({ activeIndex: index });
	};

	render() {
		const { className, classNameHead, children, withContent } = this.props;

		return (
			<div className={className}>
				<nav ref={this.navRef} className={classNameHead}>
					{
						React.Children.map(children, (child: React.ReactElement<ITabItemInternalProps>, index: number) => {
							return React.cloneElement(
								child,
								{
									active: this.isActive(index),
									onClick: async (event: React.MouseEvent) => {
										child.props.onClick && child.props.onClick();
										await this.onClick(index, event);
									},
								},
								child.props.title);
						})
					}
				</nav>
				{
					withContent &&
					React.Children.map(children, ({ props: { children } }: React.ReactElement<ITabItemInternalProps>, index: number) => {
						return this.isActive(index) && (
							<section>
								{children}
							</section>
						);
					})
				}
			</div>
		);
	}

}

export { Tabs };
